import {
  IQnACommentRequestPayload,
  IQnACommentResponseItem,
  IQnADetailPayload,
  IQnAReplyItem,
  safeStringify,
} from '@ninjacart/nc-nk-core';
import { INinjaEvent, addEventParam, useQueuedEventStore } from '@ninjacart/nc-events-sdk-v2';
import {
  AvatarUriLoad,
  KissanEventType,
  normalizeTextContent,
  truncateName,
  useGlobalLoginContext,
  useProfileStore,
} from '@ninjacart/nc-nk-store';
import {
  Avatar,
  ExpertBadge,
  Input,
  Paragraph,
  XStack,
  YStack,
  useTimeAgo,
} from '@ninjacart/nc-nk-ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard } from 'react-native';
import { useRouter } from 'solito/navigation';
// import {
//   createCommentQnA,
//   getQnACommentList,
//   onQnAReplyLike,
//   useQnaFeedStore,
// } from '@ninjacart/nc-nk-advisory';
import { ExpertAnswerCommentCard } from './expertAnswerCommentCard';
import { QnaLikeComment } from './qnaLikeComment';
import { ReplyCommentsFlashList } from './replyCommentsFlashList';
import { useParams, useSearchParams } from 'solito/navigation';

export const QnAAnswersWithComments = ({
  reply,
  showFeedback = false,
  questionId,
  pageName,
  // updateConnectionStatus,
  handleViewProfileClick,
  pageExpertName,
  createCommentQnA,
  getQnACommentList,
  onQnAReplyLike,
  useQnaFeedStore,
  index,
  language,
}: {
  reply: IQnAReplyItem;
  showFeedback?: boolean;
  questionId?: string;
  pageName?: string;
  // updateConnectionStatus?: Function;
  handleViewProfileClick?: Function;
  pageExpertName?: string;
  createCommentQnA: (config: any) => Promise<IQnACommentResponseItem>;
  getQnACommentList: (config: IQnACommentRequestPayload) => Promise<IQnACommentResponseItem[]>;
  onQnAReplyLike: (params: IQnADetailPayload) => Promise<any>;
  useQnaFeedStore: any;
  index: string | number;
  language?: string;
}) => {
  const lastReplyId = useRef(reply?.id);
  const [isExpert, setIsExpert] = useState(reply?.creatorRole === 'mods');
  const [commentReplyExpanded, setCommentReplyExpanded] = useState(false);
  const userProfile = useProfileStore((state) => state.userProfile);
  const [showFullContent, setShowFullContent] = useState(false);
  const userId = userProfile?.user?.id ?? 0;
  const setCommentInputHidden = useQnaFeedStore((state) => state.setCommentInputHidden);
  const [replyComments, setReplyComments] = useState<IQnACommentResponseItem[]>([]);
  const [commentCount, setCommentCount] = useState(reply?.commentIds?.length ?? 0);
  const [comment, setComment] = useState<string>('');
  const textAreaCommentRef = useRef<Input>(null);
  const [isLiked, setIsLiked] = useState(
    reply?.likedBy?.length && reply?.likedBy.indexOf(userId) > -1
  );
  const [likeCount, setLikeCount] = useState(reply?.likedBy?.length ?? 0);
  const isRendered = useRef(false);
  const [showFullText, setShowFullText] = useState(false);
  const hasUserLoggedIn = useProfileStore((state) => state.hasUserLoggedIn);
  const { push } = useRouter();
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const { t } = useTranslation();
  const fields = useGlobalLoginContext(['openLoginSheet', 'previousPage', 'navigationParams']);
  const setNavigationParams = fields.navigationParams.set;
  const { lang, id } = useParams();
  const seourl = id;
  const finalLang = lang || language || 'hi';

  if (reply?.id !== lastReplyId.current) {
    lastReplyId.current = reply?.id;
    setCommentReplyExpanded(false);
    setIsExpert(reply?.creatorRole === 'mods');
    if (reply?.commentCount === -1) setCommentCount(0);
  }

  const handleAnswerSuccessEvent = () => {
    const additionalDetails = [
      addEventParam('questionId', questionId),
      addEventParam('comments', comment),
      addEventParam('answerCount', replyComments),
    ];
    const qnaCommentBoxOpenSuccess: INinjaEvent = {
      name: KissanEventType.QNA_COMMENTBOXOPEN_SUCCESS,
      screenDetails: { screen: 'qna page' },
      eventParams: additionalDetails,
    };
    enqueue(qnaCommentBoxOpenSuccess);
  };

  const handleAnsweFailed = (error) => {
    const additionalDetails = [
      addEventParam('questionId', questionId),
      addEventParam('errorMessage', error.toString()),
      addEventParam('apiError', safeStringify(error)),
    ];
    const qnaCommentBoxOpenFailed: INinjaEvent = {
      name: KissanEventType.QNA_COMMENTBOXOPEN_FAIL,
      screenDetails: { screen: 'qna page' },
      eventParams: additionalDetails,
    };
    enqueue(qnaCommentBoxOpenFailed);
  };
  const initComments = async (id: string) => {
    if (!hasUserLoggedIn()) {
      return;
    }
    try {
      setCommentReplyExpanded(!commentReplyExpanded);
      const payload: IQnACommentRequestPayload = {
        id: id ?? reply?.id,
        userId: userProfile?.user?.id,
        realmId: userProfile?.realm?.id,
        limit: 10,
        page: 0,
      };
      const commentsList = await getQnACommentList(payload);
      setReplyComments(commentsList);
      handleAnswerSuccessEvent();
    } catch (error) {
      handleAnsweFailed(error);
      console.error(error);
    }
  };

  const toggleExpansion = async (id) => {
    if (!hasUserLoggedIn()) {
      fields['openLoginSheet'].set(true);
      fields['previousPage'].set('QNA Detail page');
      setNavigationParams({
        name: '',
        routeName: {
          name: `${finalLang}/qna/${seourl}`,
          params: { questionDetail: reply?.id },
        },
        description: `${t('page.toAnswerOnQnaPage')}`,
        screenName: 'QNA Detail page',
      });
      return;
    }
    setCommentReplyExpanded(!commentReplyExpanded);
    await initComments(id);
  };

  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', () => {
      // setCommentInputHidden(true);
    });
    const keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', () => {
      setCommentInputHidden(false);
    });

    return () => {
      keyboardDidHideListener.remove();
      keyboardDidShowListener.remove();
    };
  }, []);

  const addCommentToReply = async (commentOnReply) => {
    console.log('Add Comment to reply called here ');
    console.log('Comment:', comment);
    if (!hasUserLoggedIn()) {
      fields['openLoginSheet'].set(true);
      fields['previousPage'].set('QNA Detail page');
      setNavigationParams({
        name: '',
        routeName: {
          name: `${finalLang}/qna/${seourl}`,
          params: { questionDetail: reply?.id },
        },
        description: `${t('page.toAnswerOnQnaPage')}`,
        screenName: 'QNA Detail page',
      });
      return;
    }
    if (comment.trim()?.length == 0) {
      return;
    }
    try {
      const config = {
        formData: {
          userId: userProfile?.user?.id,
          realmId: userProfile?.realm?.id,
        },
        payload: {
          parentid: commentOnReply?.id,
          comment: comment,
          creatorid: userProfile?.user?.id,
          hidden: false,
        },
      };
      console.log('config', config);
      const addNewComment = await createCommentQnA(config);
      console.log('New Comment added against Reply', addNewComment);
      if (addNewComment) {
        textAreaCommentRef?.current?.clear();
        const mergeComments = [addNewComment, ...replyComments];
        setReplyComments(mergeComments);
        setCommentCount(mergeComments?.length ?? 0);
        console.log('Comment count => ', mergeComments?.length);

        reply.commentCount = mergeComments?.length;
        setComment('');
      }
    } catch (error) {
      console.error('Add New Comment in QnA Error', error);
    }
  };

  const handleAnswerLikedEvent = () => {
    const qnaAnswerLiked: INinjaEvent = {
      name: KissanEventType.QNA_ANSWER_LIKED,
      screenDetails: { screen: 'qna page' },
      eventParams: [addEventParam('questionId', questionId)],
    };
    enqueue(qnaAnswerLiked);
  };

  const handleAnswerUnlikedEvent = () => {
    const qnaAnswerDisLiked: INinjaEvent = {
      name: KissanEventType.QNA_ANSWER_DISLIKED,
      screenDetails: { screen: 'qna page' },
      eventParams: [addEventParam('questionId', questionId)],
    };
    enqueue(qnaAnswerDisLiked);
  };

  const onAnswerLike = async (reply: IQnAReplyItem) => {
    if (!hasUserLoggedIn()) {
      fields['openLoginSheet'].set(true);
      fields['previousPage'].set('QNA Detail page');
      setNavigationParams({
        name: '',
        routeName: {
          name: `${finalLang}/qna/${seourl}`,
          params: { questionDetail: reply?.id },
        },
        description: `${t('page.likeAnswerForQuestion')}`,
        screenName: 'QNA Detail page',
      });
      return;
    }
    const currentLikeStateFlip = isLiked;
    try {
      if (!reply?.id) return;

      const params: IQnADetailPayload = {
        userId: useProfileStore?.getState()?.userProfile?.user?.id,
        realmId: useProfileStore?.getState()?.userProfile?.realm?.id,
        id: reply?.id,
      };

      setIsLiked(!isLiked);
      const onlike = await onQnAReplyLike(params);

      if (onlike === 'Liked') {
        handleAnswerLikedEvent();
        setIsLiked(true);
        return;
      }

      if (onlike === 'Unliked') {
        handleAnswerUnlikedEvent();
        setIsLiked(false);
        return;
      }
    } catch (err) {
      setIsLiked(currentLikeStateFlip);
      console.error(err);
    }
  };

  useEffect(() => {
    if (isRendered.current) {
      setLikeCount(isLiked ? likeCount + 1 : Math.max(likeCount - 1, 0));
    }
    isRendered.current = true;
  }, [isLiked]);

  const handleReadMoreClickEvent = () => {
    const qnaReadMoreClickEvent: INinjaEvent = {
      name: KissanEventType.QNA_READ_MORE_CLICK,
      screenDetails: { screen: 'qna page' },
      eventParams: [addEventParam('questionId', questionId)],
    };
    enqueue(qnaReadMoreClickEvent);
  };
  const handleReadLessClickEvent = () => {
    const qnaReadLessClickEvent: INinjaEvent = {
      name: KissanEventType.QNA_READ_LESS_CLICK,
      screenDetails: { screen: 'qna page' },
      eventParams: [addEventParam('questionId', questionId)],
    };
    enqueue(qnaReadLessClickEvent);
  };

  const showFullTextFunction = () => {
    setShowFullText(!showFullText);
    if (showFullText) {
      handleReadLessClickEvent();
    } else {
      handleReadMoreClickEvent();
    }
  };

  // const handleButtonClick = async (event: GestureResponderEvent) => {
  //   if (!hasUserLoggedIn()) {
  //     handleNavigation(
  //       '',
  //       {
  //         name: `profile/[id]`,
  //         params: {
  //           id: reply?.updatedBy
  //         },
  //       },
  //       `${t('page.toFollow')}`,
  //       'QnA-Detail-Page'
  //     );
  //     return;
  //   } else {
  //     await handleContactPermission();
  //     // if (updateConnectionStatus) {
  //     //   const isFollow = updateConnectionStatus(reply?.following, reply?.updatedBy);
  //     // }
  //   }
  // }

  return (
    <YStack>
      {isExpert && (
        <ExpertAnswerCommentCard
          expert={reply}
          pageName={pageName}
          pageExpertName={pageExpertName}
          index={index}
        />
      )}

      {!isExpert && (
        <YStack marginHorizontal={15}>
          <XStack marginTop={20}>
            <Avatar
              circular
              size="$3"
              marginTop={10}
              onPress={() => handleViewProfileClick?.(reply?.updatedBy)}
              testID={`kisan_tId_view_profile_click${index}`}
            >
              <Avatar.Image
                accessibilityLabel="Nate Wienert"
                source={{
                  uri: AvatarUriLoad(reply),
                }}
              />
              <Avatar.Fallback delayMs={600} />
            </Avatar>
            <YStack
              padding={isExpert ? 15 : 10}
              marginLeft={10}
              borderRadius={10}
              flex={1}
              paddingBottom={10}
              borderWidth={1}
              backgroundColor={isExpert ? '$expectAnswerCardBg' : '$placeholderColorBg'}
              borderColor={isExpert ? '#CAE9DD' : '#F3F4F6'}
            >
              <XStack flexDirection="row" justifyContent="space-between" alignItems="center">
                <XStack flexDirection="row" alignItems="center">
                  <Paragraph
                    fontSize={16}
                    color={'$commentSectionTextColor'}
                    marginBottom={1}
                    fontWeight={'600'}
                    paddingBottom={0}
                    paddingRight={!isExpert && 10}
                    onPress={() => handleViewProfileClick?.(reply?.updatedBy!)}
                    testID={`kisan_tId_view_profile_click${index}`}
                     numberOfLines={1} ellipsizeMode="tail"
                  >
                    {reply?.expertName && reply?.expertName.length > 0
                      ? reply.expertName
                      : reply?.author?.name}
                  </Paragraph>

                  {isExpert && <ExpertBadge color={'#fff'} fontSize={12} name={t('page.expert')} />}

                  {/* Commented this code, may be useful in future */}
                  {/* {!isExpert && reply?.updatedBy !== userProfile?.user?.id &&
                    <Paragraph onPress={handleButtonClick} color={'$primary'} fontSize={14} fontWeight={'500'}>
                      {reply?.following ? t('page.following') : t('page.follow')}
                    </Paragraph>
                  } */}
                </XStack>
              </XStack>
              <XStack>
                <Paragraph fontSize={11} fontWeight={'500'} color={'$commentSectionTextColor'}>
                  {reply?.timestamp && useTimeAgo(reply?.timestamp)}
                </Paragraph>
              </XStack>

              <Paragraph
                color={isExpert ? '$expertNameColor' : '$commentSectionTextColor'}
                style={{
                  fontSize: 14,
                  fontWeight: '500',
                  marginTop: 8,
                }}
              >
                {showFullText ? reply.body : normalizeTextContent(reply?.body, false)}
                {reply?.body?.length > 100 && (
                  <Paragraph
                    onPress={() => showFullTextFunction()}
                    color="$primary"
                    fontSize={14}
                    fontWeight="500"
                    marginTop={8}
                    paddingLeft={3}
                    testID={`kisan_tId_read_more_less_btn_${index}`}
                  >
                    {showFullText ? t('page.readLess') : `...${t('page.readMore')}`}
                  </Paragraph>
                )}
              </Paragraph>
            </YStack>
          </XStack>
        </YStack>
      )}

      {showFeedback && (
        <QnaLikeComment
          reply={reply}
          onAnswerLike={onAnswerLike}
          likeCount={likeCount}
          commentCount={commentCount}
          toggleExpansion={toggleExpansion}
          isLiked={isLiked}
          isExpert={isExpert}
          index={index}
        />
      )}

      {commentReplyExpanded && (
        <ReplyCommentsFlashList
          reply={reply}
          replyComments={replyComments}
          addCommentToReply={addCommentToReply}
          setComment={setComment}
          comment={comment}
          textAreaCommentRef={textAreaCommentRef}
          handleViewProfileClick={handleViewProfileClick!}
          useQnaFeedStore={useQnaFeedStore}
          index={index}
        />
      )}
    </YStack>
  );
};
